import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, LoadingOverlay } from 'dashboard/components/common';
import MovementDialog from './MovementDialog';
import TransferStockDialog from './TransferStockDialog';

/**
 * Stock matrix: rows are variants, columns are warehouses. Each cell
 * is a menu button that opens the movement dialog for that pair.
 */
export default function InventoryTab({ brandId }) {
  const [matrix, setMatrix] = useState({ warehouses: [], variants: [], levels: [] });
  const [productFilter, setProductFilter] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [cellMenu, setCellMenu] = useState(null);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.getShopStockMatrix({ brand_id: brandId, product_id: productFilter || undefined })
      .then((res) => setMatrix(res || { warehouses: [], variants: [], levels: [] }))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [brandId, productFilter]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getShopStockMatrix({ brand_id: brandId, product_id: productFilter || undefined })
      .then((res) => { if (!cancelled) setMatrix(res || { warehouses: [], variants: [], levels: [] }); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load', { toastId: 'inventory-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId, productFilter]);
  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    API.listShopProducts({ brand_id: brandId })
      .then((res) => { if (!cancelled) setProducts(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [brandId]);

  const variantsById = useMemo(
    () => Object.fromEntries((matrix.variants || []).map((v) => [v._id, v])),
    [matrix.variants],
  );
  const warehousesById = useMemo(
    () => Object.fromEntries((matrix.warehouses || []).map((w) => [w._id, w])),
    [matrix.warehouses],
  );
  const levelsByKey = useMemo(() => {
    const m = {};
    (matrix.levels || []).forEach((l) => { m[`${l.variant_id}:${l.warehouse_id}`] = l; });
    return m;
  }, [matrix.levels]);

  const openAdjust = (variant_id, warehouse_id) => {
    setEditingLevel(
      levelsByKey[`${variant_id}:${warehouse_id}`]
      || { variant_id, warehouse_id, on_hand: 0, reserved: 0, incoming: 0 },
    );
    setDlgOpen(true);
    setCellMenu(null);
  };

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap='10px' mb='14px'>
        <Box>
          <Typography fontWeight='700'>Inventory</Typography>
          <Typography variant='body2' color='text.secondary'>
            Stock by SKU × location. Click a cell to receive production, adjust, or schedule incoming.
          </Typography>
        </Box>
        <Stack direction='row' spacing='8px' flexWrap='wrap'>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel shrink>Product</InputLabel>
            <Select
              label='Product'
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value=''>All products</MenuItem>
              {products.map((p) => (
                <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setTransferOpen(true)}
            disabled={matrix.warehouses.length < 2 || matrix.variants.length === 0}
          >
            Transfer stock
          </Button>
        </Stack>
      </Stack>

      <TransferStockDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        brandId={brandId}
        warehouses={matrix.warehouses}
        variants={matrix.variants}
        levelsByKey={levelsByKey}
        onTransferred={reload}
      />

      <LoadingOverlay loading={loading && matrix.variants.length === 0 && matrix.warehouses.length === 0} minHeight={180}>
        {!loading && matrix.warehouses.length === 0 && (
          <EmptyState>No active warehouses. Add one in the Warehouses tab first.</EmptyState>
        )}
        {!loading && matrix.warehouses.length > 0 && matrix.variants.length === 0 && (
          <EmptyState>No variants. Add a product with at least one variant.</EmptyState>
        )}
      </LoadingOverlay>

      {matrix.warehouses.length > 0 && matrix.variants.length > 0 && (
        <TableContainer
          component={Box}
          sx={{
            border: '1px solid', borderColor: 'divider',
            borderRadius: '12px', overflowX: 'auto',
          }}
        >
          <Table size='small'>
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell>Variant</TableCell>
                {matrix.warehouses.map((w) => (
                  <TableCell key={w._id} align='center'>{w.code || w.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matrix.variants.map((v) => (
                <TableRow key={v._id}>
                  <TableCell>
                    <Typography variant='body2' fontWeight='600'>{v.sku || '—'}</Typography>
                    {v.attributes && Object.keys(v.attributes).length > 0 && (
                      <Typography variant='caption' color='text.secondary'>
                        {Object.entries(v.attributes).map(([k, val]) => `${k}: ${val}`).join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  {matrix.warehouses.map((w) => {
                    const lvl = levelsByKey[`${v._id}:${w._id}`];
                    const available = lvl?.available ?? 0;
                    const lowStock = lvl && lvl.low_stock_threshold > 0 && available <= lvl.low_stock_threshold;
                    const cellId = `${v._id}:${w._id}`;
                    const stockColor = available > 0
                      ? (lowStock ? 'warning.main' : 'success.main')
                      : 'text.disabled';
                    return (
                      <TableCell key={w._id} align='center'>
                        <Button
                          size='small'
                          variant='text'
                          endIcon={<ArrowDropDownIcon />}
                          onClick={(e) => setCellMenu({ id: cellId, anchor: e.currentTarget, v, w })}
                        >
                          <Typography component='span' fontWeight='700' color={stockColor}>
                            {available}
                          </Typography>
                          {(lvl?.reserved > 0 || lvl?.incoming > 0) && (
                            <Typography
                              component='span'
                              variant='caption'
                              color='text.secondary'
                              ml='4px'
                            >
                              {lvl.reserved > 0 ? `R:${lvl.reserved}` : ''}
                              {lvl.incoming > 0 ? ` I:${lvl.incoming}` : ''}
                            </Typography>
                          )}
                        </Button>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Menu
            anchorEl={cellMenu?.anchor}
            open={Boolean(cellMenu)}
            onClose={() => setCellMenu(null)}
          >
            <MenuItem onClick={() => openAdjust(cellMenu.v._id, cellMenu.w._id)}>
              Adjust / receive…
            </MenuItem>
          </Menu>
        </TableContainer>
      )}

      {editingLevel && (
        <MovementDialog
          open={dlgOpen}
          onClose={() => setDlgOpen(false)}
          brandId={brandId}
          level={editingLevel}
          variantsById={variantsById}
          warehousesById={warehousesById}
          onSaved={reload}
        />
      )}
    </Box>
  );
}
